import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheck, Ban, FileWarning, Printer, Loader2 } from 'lucide-react';
import { T, TIPOGRAFIA, glassLight, primaryButton, Badge } from '../ui';
import { LOGO_DATA_URI } from '../Logo';
import { fetchPedidoPorToken } from './gasData';
import { esWOP, etiquetaTipo, imprimirPedido, suplidorDe } from './pedidoDocumento';
import type { Pedido } from '../types';

// Etiqueta legible de cada producto, duplicada a propósito en forma mínima:
// esta pantalla es pública y no debe arrastrar la lógica del módulo interno.
const PRODUCTO_LABEL: Record<string, string> = {
  jetA1: 'Jet A-1 / Kerosen',
  gasolinaRegular: 'Gasolina Regular',
  gasolinaPremium: 'Gasolina Premium',
  gasoilRegular: 'Gasoil Regular',
  gasoilOptimo: 'Gasoil Óptimo',
  fuelOilReg: 'Fuel Oil Reg.',
  fuelOilPrem: 'Fuel Oil Prem.',
  glp: 'GLP'
};
const EMPRESA_NOMBRE = 'Junquito Gas, S.R.L.';
const fmtGal = (n: number) => new Intl.NumberFormat('es-DO').format(n || 0);
const fmtFecha = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};
function Marco({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    minHeight: '100vh',
    background: '#FFFCF8',
    fontFamily: TIPOGRAFIA,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px'
  }} data-id="element-1320" data-mp="r04z0x6r2bm0">

      <img src={LOGO_DATA_URI} alt="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3naxhgyiiyi6ejjvg5a7v7/14d78902-f966-463d-9256-d7f4b068c9a5/pasted-image.png" style={{
      height: 72,
      marginBottom: 26
    }} data-id="element-1321" data-mp="10tc2gchxjhjg" />

      <div style={{
      width: '100%',
      maxWidth: 720
    }} data-id="element-1322" data-mp="3ghvm8l1i33zg">

        {children}
      </div>
    </div>;
}
function Dato({
  label,
  valor
}: {
  label: string;
  valor: string;
}) {
  return <div data-id="element-1323" data-mp="1fgfoh4nb0vby">
      <div style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 3
    }} data-id="element-1324" data-mp="1fgxqkhwne3rs">

        {label}
      </div>
      <div style={{
      fontSize: 14,
      color: T.text,
      fontWeight: 600
    }} data-id="element-1325" data-mp="1ofsvlqnyh7ti">

        {valor || '—'}
      </div>
    </div>;
}

// Pantalla pública: se muestra ANTES del login cuando la URL trae
// ?verificar=<token>. Es la copia digital a la que lleva el QR impreso en el
// formulario, para que quien recibe el papel pueda cotejarlo contra el
// sistema sin tener usuario.
export function VerificarPedido({
  token
}: {
  token: string;
}) {
  const [estado, setEstado] = useState<'cargando' | 'listo' | 'error'>('cargando');
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const res = await fetchPedidoPorToken(token);
      if (cancelado) return;
      if (res.error) {
        setError(res.error);
        setEstado('error');
        return;
      }
      // Un borrador no es un documento emitido: aunque alguien conociera el
      // token, no debe poder verlo hasta que esté autorizado.
      if (!res.pedido || res.pedido.estado === 'borrador') {
        setPedido(null);
        setEstado('listo');
        return;
      }
      setPedido(res.pedido);
      setEstado('listo');
    })();
    return () => {
      cancelado = true;
    };
  }, [token]);
  if (estado === 'cargando') {
    return <Marco data-id="element-1326" data-mp="3597um2lwvt5j">
        <div style={{
        ...glassLight,
        padding: 40,
        textAlign: 'center',
        color: T.textSecondary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12
      }} data-id="element-1327" data-mp="358zhqapsgnuc">

          <Loader2 size={26} style={{
          color: T.accent,
          animation: 'spin 1s linear infinite'
        }} data-id="element-1328" data-mp="1lpd8oehxzfz" />

          <style data-id="element-1329" data-mp="3swpfrfao7szj">{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
          Verificando documento…
        </div>
      </Marco>;
  }
  if (estado === 'error' || !pedido) {
    return <Marco data-id="element-1330" data-mp="3ih6qgbtwriem">
        <div style={{
        ...glassLight,
        padding: 40,
        textAlign: 'center'
      }} data-id="element-1331" data-mp="3idsnp2hc176n">

          <FileWarning size={34} style={{
          color: T.danger,
          marginBottom: 14
        }} data-id="element-1332" data-mp="34igs6svcrcoo" />

          <div style={{
          fontSize: 19,
          fontWeight: 800,
          color: T.text,
          marginBottom: 8
        }} data-id="element-1333" data-mp="1w3vegexr12">

            Documento no encontrado
          </div>
          <div style={{
          fontSize: 14,
          color: T.textSecondary,
          lineHeight: 1.5
        }} data-id="element-1334" data-mp="52tl9otlmh1i">

            {error ?? 'Este código no corresponde a ninguna orden de pedido emitida. ' + 'Si lo escaneaste de un documento impreso, verifícalo con la empresa ' + 'antes de despachar.'}
          </div>
        </div>
      </Marco>;
  }
  const anulado = pedido.estado === 'anulado';
  const total = pedido.lineas.reduce((s, l) => s + (l.galones || 0), 0);
  return <Marco data-id="element-1335" data-mp="28g2ouxq9l69t">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1336" data-mp="28fsuyz23meyu">

        {/* Veredicto arriba del todo: es lo único que importa de un vistazo */}
        <div style={{
        ...glassLight,
        padding: '22px 24px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        borderColor: anulado ? `${T.danger}44` : `${T.ok}44`,
        background: anulado ? T.dangerSoft : T.okSoft
      }} data-id="element-1337" data-mp="2i9rt7mz1xeli">

          {anulado ? <Ban size={30} style={{
          color: T.danger,
          flexShrink: 0
        }} data-id="element-1338" data-mp="1kmayqb61tkdl" /> : <BadgeCheck size={30} style={{
          color: T.ok,
          flexShrink: 0
        }} data-id="element-1339" data-mp="1zf4k2d804uq6" />}
          <div data-id="element-1340" data-mp="1ukh3d0j8p4m5">
            <div style={{
            fontSize: 18,
            fontWeight: 800,
            color: anulado ? T.danger : T.ok
          }} data-id="element-1341" data-mp="3aw482wnbgnee">

              {anulado ? 'Orden anulada' : 'Documento auténtico'}
            </div>
            <div style={{
            fontSize: 13,
            color: T.textSecondary,
            marginTop: 2
          }} data-id="element-1342" data-mp="3d197t1kyp6bt">

              {anulado ? 'Esta orden fue anulada. No debe despacharse contra este documento.' : `Autorizada por ${pedido.autorizadoPor || '—'} el ${fmtFecha(pedido.autorizadoFecha)}.`}
            </div>
          </div>
        </div>

        <div style={{
        ...glassLight,
        padding: 24
      }} data-id="element-1343" data-mp="3rve3jmav8moh">

          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 20
        }} data-id="element-1344" data-mp="2ubt4y7lt11s8">

            <span style={{
            fontSize: 21,
            fontWeight: 800,
            color: T.text
          }} data-id="element-1345" data-mp="3474l8ucv2h23">

              Orden {pedido.numeroOrden}
            </span>
            <Badge label={etiquetaTipo(pedido.tipo)} color={esWOP(pedido.tipo) ? T.textMuted : T.brand} soft={esWOP(pedido.tipo) ? T.surfaceSubtle : T.accentSoft} dot={false} data-id="element-1346" data-mp="2bka4o8j601tw" />

          </div>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
          gap: 18,
          marginBottom: 24
        }} data-id="element-1347" data-mp="xnz0u87lrx2p">

            <Dato label="Fecha" valor={fmtFecha(pedido.fecha)} data-id="element-1348" data-mp="omsgfo1u2246" />
            <Dato label="Suplidor" valor={suplidorDe(pedido.tipo).razonSocial} data-id="element-1349" data-mp="okxwabydye15" />
            <Dato label="Conductor" valor={pedido.conductor} data-id="element-1350" data-mp="jmwdzt8saiqq" />
            <Dato label="Pase del chofer" valor={pedido.paseChofer} data-id="element-1351" data-mp="eocstv20lazf" />
            <Dato label="Ficha de la cola" valor={pedido.fichaCola} data-id="element-1352" data-mp="eqfpgxxoa2d4" />
            <Dato label="Placa del camión" valor={pedido.placaCamion} data-id="element-1353" data-mp="9wbhqcgl1qaz" />
            <Dato label="Total" valor={`${fmtGal(total)} galones`} data-id="element-1354" data-mp="5vhuzmevmhjo" />
          </div>

          <div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: T.textMuted,
          marginBottom: 10
        }} data-id="element-1355" data-mp="xq2orabktey">

            Detalle del despacho
          </div>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 24
        }} data-id="element-1356" data-mp="3n6hi32gayf4b">

            {pedido.lineas.map((l, i) => <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            alignItems: 'baseline',
            flexWrap: 'wrap',
            padding: '10px 14px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd
          }} data-id="element-1357" data-mp="1ju9lcdwwoa1l" data-mpi={i}>

                <div data-id="element-1358" data-mp="10wgu48vn9ok1">
                  <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: T.text
              }} data-id="element-1359" data-mp="10wyw7m4zmwzv">

                    {l.destino}
                  </div>
                  <div style={{
                fontSize: 12,
                color: T.textMuted
              }} data-id="element-1360" data-mp="s4om04u3erim">

                    {PRODUCTO_LABEL[l.producto] ?? l.producto}
                    {esWOP(pedido.tipo) ? ` · Compart. ${l.compartimiento}` : ''}
                    {l.rncDestino ? ` · RNC ${l.rncDestino}` : ''}
                  </div>
                </div>
                <div style={{
              fontSize: 15,
              fontWeight: 800,
              color: T.accent,
              whiteSpace: 'nowrap'
            }} data-id="element-1361" data-mp="3qnyeilzri8h1">

                  {fmtGal(l.galones)} gal
                </div>
              </div>)}
          </div>

          <button type="button" onClick={() => imprimirPedido(pedido)} style={{
          ...primaryButton,
          width: '100%',
          justifyContent: 'center'
        }} data-id="element-1362" data-mp="3l3erzhrnw8q">

            <Printer size={15} data-id="element-1363" data-mp="dgoc3kbw48c5" /> Ver / descargar el formulario
          </button>
        </div>

        <div style={{
        textAlign: 'center',
        fontSize: 11,
        color: T.textMuted,
        marginTop: 18,
        lineHeight: 1.6
      }} data-id="element-1364" data-mp="r7jlqyu7qtgs">

          Código de verificación{' '}
          <span style={{
          fontFamily: 'monospace'
        }} data-id="element-1365" data-mp="116qzhn4v0q4f">
            {pedido.token.slice(0, 12)}
          </span>
          <br data-id="element-1366" data-mp="1pjbg2xwlyn0p" />
          Documento emitido por {EMPRESA_NOMBRE}.
        </div>
      </motion.div>
    </Marco>;
}